import axios from "axios";
import React, { useCallback, useEffect } from "react";
import { endpoints } from "../../../../endpoints/endpoints";
import IDayliPlan from "../interface/iDayliPlan";
import { User, AppContext } from "../../../../context/appContext";
import transformDateToDe from "../../../../functions/transformDateToDe";
import { ShowFileDialogProps } from "../../../searchParticipant/interfaces/interfaces";

export const DayliPlanContext = React.createContext<any>({});

const DayliPlanProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [isLoading, setIsLoading] = React.useState(false);

  const [dayliPlan, setDayliPlan] = React.useState<IDayliPlan>({
    fromDate: new Date().toISOString().split("T")[0],
    tillDate: new Date().toISOString().split("T")[0],
    appointments: [],
  });

  const [fetched, setFetched] = React.useState<boolean>(false);

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    setIsLoading(true);
    setDayliPlan({
      ...dayliPlan,
      appointments: [],
    });

    await axios
      .post(endpoints.coach.dailyPlan, {
        sessionId: user.sessionId,
        fromDate: dayliPlan.fromDate,
        tillDate: dayliPlan.tillDate,
      })
      .then((response) => {
        checkSession(response);
        let tmpDate: string = "";

        // extend appointments with additional properties
        response.data.data.map((appointment: any) => {
          appointment.showDateLine = false;
          appointment.showAllAppointments = false;

          if (appointment.date !== tmpDate) {
            tmpDate = appointment.date;
            appointment.showDateLine = true;
          }

          return appointment;
        });

        setDayliPlan({ ...dayliPlan, appointments: response.data.data });
        setIsLoading(false);
        setFetched(true);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [checkSession, dayliPlan, user.sessionId]);

  const [showFileDialog, setShowFileDialog] =
    React.useState<ShowFileDialogProps>({
      open: false,
      participant: null,
    });

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [maxShownAppointments, setMaxShownAppointments] =
    React.useState<number>(5);

  // export to csv file
  const exportToCsv = React.useCallback((): void => {
    // Define headers for each column
    const headers = [
      "Datum",
      "Zeitfenster",
      "Wochentag",
      "Teilnehmername",
      "Teilnehmer-Email",
      "Teilnehmer-Qualifikationsende",
      "Teilnehmer-Telefonnummer",
      "Teilnehmer-Qualifikation",
    ];

    // remove all chars except numbers from phone number
    const cleanPhoneNumber = (phoneNumber: string): string => {
      return phoneNumber.replace(/[^0-9]/g, "");
    };

    // Combine headers with data using map
    const csvContent = dayliPlan.appointments.map((appointment: any) => {
      return `${transformDateToDe(appointment.date)};${appointment.timeSlot};${
        appointment.weekDay
      };${appointment.participantFullName};${appointment.participantEmail};${
        appointment.participantEndOfQualification !== null &&
        transformDateToDe(appointment.participantEndOfQualification)
      };${cleanPhoneNumber(appointment.participantPhoneNumber)};${
        appointment.participantQualification
      }`;
    });

    // Add headers to the beginning of the CSV content
    csvContent.unshift(headers.join(";")); // Join headers with semicolon separator

    const csv = csvContent.join("\n");

    const csvFile = new Blob([csv], { type: "text/csv" });
    const downloadLink = document.createElement("a");
    downloadLink.download = `Deine Coachingtermine vom ${transformDateToDe(
      dayliPlan.fromDate
    )}-${transformDateToDe(dayliPlan.tillDate)}.csv`;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }, [dayliPlan.appointments, dayliPlan.fromDate, dayliPlan.tillDate]);

  const getTileBackgroundColor = (
    appointment: any
  ): "success.light" | "warning.light" | "error.light" | "#eee" => {
    switch (appointment.status) {
      case "G":
        return "success.light";
      case "V":
        return "error.light";
      case "K":
        return "warning.light";
      default:
        return "#eee";
    }
  };

  const value: {} = React.useMemo(
    () => ({
      isLoading,
      setIsLoading,
      dayliPlan,
      setDayliPlan,
      fetched,
      setFetched,
      fetchAppointments,
      maxShownAppointments,
      setMaxShownAppointments,
      exportToCsv,
      showFileDialog,
      setShowFileDialog,
      getTileBackgroundColor,
    }),
    [
      isLoading,
      dayliPlan,
      fetched,
      fetchAppointments,
      maxShownAppointments,
      exportToCsv,
      showFileDialog,
    ]
  );

  return (
    <DayliPlanContext.Provider value={value}>
      {children}
    </DayliPlanContext.Provider>
  );
};

export default DayliPlanProvider;
